/**
 * Die Kopfzeile kennt ihren Platz. (22.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin, mit einem Foto seines Telefons: *„die Kopfzeile ist ebenfalls eine
 * Katastrophe. Die gehört auch überarbeitet."*
 *
 * Nachgemessen bei 390 Punkten Breite, im vollen Zustand:
 *
 *      68..176  Projekt „Survival Training"   108
 *     190..212  Spiegel-Stern                  22
 *     226..259  Tasks-Puls „2"                 32
 *     273..373  Aufnahme-Status               101
 *     387..419  Glocke „9+"            RAGT RAUS
 *     433..465  „Neue Version"        GANZ DRAUSSEN
 *
 * Die Kopfzeile braucht 397 Punkte und hat 390. Die Glocke steht halb
 * draußen — und die Update-Pille liegt **43 Punkte außerhalb des
 * Bildschirms**. Der Knopf „Neue Version" war auf dem Telefon nicht
 * antippbar. (Am selben Tag hing Martins App auf pre.3139, während pre.3140
 * längst gebaut war.)
 *
 * ## Warum es eine Rangfolge braucht und keine neue Anordnung
 *
 * Die Zahl der Anzeigen ist nicht fest: Aufnahme, Glocke und Update
 * erscheinen unabhängig voneinander. Jede feste Aufteilung ist für irgendeine
 * Kombination falsch. Was gebraucht wird, ist eine Entscheidung: **wenn es eng
 * wird, wer geht zuerst?**
 *
 * ## Seit 11.10.2026: der Kopf trägt nur noch das, was nie weichen darf
 *
 * Martin: *„Der Header sieht immer noch schrecklich aus mit diesen vielen
 * Punkten da oben."* Projekt, Spiegel, Mac und Aufgaben-Puls sind aus der Leiste
 * gezogen (Projekt: Zeile auf Zuhause; Spiegel, Mac, Aufgaben: Zeilen in der
 * Lage unter der Glocke; die vier Zahlen: Zuhause). Übrig sind links Menü, Stern
 * und Name, rechts Aufnahme, Glocke und „Neue Version" — und nur die Marke
 * (Stern, Name) kann bei Enge noch weichen.
 *
 * ⚠️ Drei Dinge gehen NIE. Der Aufnahme-Status, weil „nimmt es gerade auf?"
 *    die teuerste offene Frage dieses Systems ist (Ticket 2229, Martin:
 *    *„Kein Zeichen ohne Text."*). Die Glocke, weil dort Rückfragen liegen,
 *    die auf eine Antwort warten. Und die Update-Pille, weil eine App, die
 *    ihre eigene Erneuerung nicht anbieten kann, auf einem alten Stand
 *    festhängt — genau der Fall, der am 22.09. gemessen wurde (die Pille lag
 *    43 Punkte außerhalb des Bildschirms).
 */

/** Ein Posten in der Kopfzeile, so wie er gemessen wurde. */
export interface KopfPosten {
  id: string;
  /** Gemessene Breite in Punkten. */
  breite: number;
  /**
   * Kleinste Breite, auf die sich der Posten selbst kürzen kann (Punkte).
   * (05.10.2026) Das aktive Projekt dehnt und kürzt sich — seine gemessene
   * Breite ist darum immer schon die, die gerade übrig ist, und würde nie
   * „zu breit“ sein. Gerechnet wird mit seiner Mindestbreite: passt auch die
   * nicht mehr, geht der ganze Posten. Fehlt der Wert, gilt `breite`.
   */
  min?: number;
  /**
   * Unantastbar? Diese Posten weichen nie — lieber wird alles andere
   * ausgeblendet.
   */
  fest?: boolean;
}

/**
 * Die Rangfolge der Verzichtbaren, am ehesten entbehrlich zuerst.
 *
 * ⚠️ Sie steht hier und nicht im CSS, weil sie eine INHALTLICHE Aussage ist:
 *    wer die Reihenfolge ändert, ändert, was die App für wichtig hält — das
 *    gehört in den Blick und nicht in eine Zeile Layout.
 */
export const WEICHT_ZUERST: readonly string[] = [
  /*
   * Der Stern geht zuerst: der Weg nach Hause steht auch im Menü, und der Name
   * daneben sagt, wo man ist. Eine Kopie zu entfernen nimmt niemandem etwas.
   *
   * ⚠️ `brand-home` führt auch zur Startseite. Er ist nicht der einzige
   *    Weg — Zuhause steht im Menü.
   */
  "brand-home",
  /* 16.10.2026: die Marke „Entwicklung <Zahl>“ (nur Konto 1, nur bei laufenden Arbeiten) ist Beiwerk; Aufnahme, Glocke und
     Update gehen vor. Sie weicht nach dem Stern, vor dem Namen. */
  "topbar-entwicklung",
  /* 20.10.2026: die Marke „Offen <Zahl>“ (offene Fragen an Martin, lib/offene-fragen.ts) — wichtiger als „Entwicklung“, darum
     weicht sie erst danach; die Glocke und die Aufnahme gehen weiter vor. */
  "topbar-offen",
  /* Der Bot-Name bleibt am längsten: er ist Martins eigener, nicht der
     generische Produktname. */
  "topbar-bot-name",
];

/**
 * Wer muss weichen, damit alles hineinpasst?
 *
 * Gibt die IDs zurück, die ausgeblendet werden sollen — leer, wenn alles
 * passt. Rein und ohne Seiteneffekt, damit die Entscheidung prüfbar ist.
 *
 * @param posten   Die sichtbaren Posten mit ihrer gemessenen Breite.
 * @param budget   Wie viel Platz zur Verfügung steht (Punkte).
 * @param abstand  Lücke zwischen zwei Posten (Punkte).
 */
export function wasWeichenMuss(
  posten: readonly KopfPosten[],
  budget: number,
  abstand: number,
): string[] {
  const braucht = (liste: readonly KopfPosten[]): number =>
    liste.reduce((s, p) => s + (p.min ?? p.breite), 0) +
    Math.max(0, liste.length - 1) * abstand;

  let bleibt = [...posten];
  const weg: string[] = [];
  /*
   * ⚠️ Nach der Rangfolge, nicht nach der Breite. Der breiteste Posten
   *    wegzunehmen wäre rechnerisch am wirksamsten und inhaltlich falsch —
   *    der breiteste ist hier der Aufnahme-Status, und genau der muss
   *    bleiben.
   */
  for (const id of WEICHT_ZUERST) {
    if (braucht(bleibt) <= budget) break;
    const i = bleibt.findIndex((p) => p.id === id && !p.fest);
    if (i < 0) continue;
    weg.push(id);
    bleibt = bleibt.filter((_, k) => k !== i);
  }
  /*
   * Auch danach noch zu eng? Dann geht alles Nicht-Feste, in der Reihenfolge
   * von hinten — ein Posten, der nur zur Hälfte sichtbar ist, nützt
   * niemandem und verdeckt den daneben.
   */
  while (braucht(bleibt) > budget) {
    const i = [...bleibt].reverse().findIndex((p) => !p.fest);
    if (i < 0) break;
    const echterIndex = bleibt.length - 1 - i;
    const p = bleibt[echterIndex];
    if (!p) break;
    weg.push(p.id);
    bleibt = bleibt.filter((_, k) => k !== echterIndex);
  }
  return weg;
}
